import React from 'react'
import {
	differenceInDays,
	endOfDay,
	formatDistanceToNow,
	isAfter,
	isBefore,
	startOfDay,
} from 'date-fns'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/shared/components/ui/badge'
import { intlFormat, isBetween, utcToZonedTime } from '@/shared/tools/dates'

export const PricingCampaignPeriodMessage = ({
	startDate,
	endDate,
	isForceInactive,
}: {
	startDate?: string
	endDate?: string
	isForceInactive?: boolean
}) => {
	const { t } = useTranslation()

	const now = new Date()
	const start = startDate
		? startOfDay(utcToZonedTime(new Date(startDate), 'UTC'))
		: undefined
	const end = endDate
		? endOfDay(utcToZonedTime(new Date(endDate), 'UTC'))
		: undefined
	const { isActive, isNotStarted, isFinished } = getPricingCampaignStatus(
		now,
		start,
		end
	)
	if (isForceInactive) {
		return (
			<Badge size='sm' variant='destructive' className='text-xs font-medium'>
				{t('pc.period.is_force_inactive', {
					defaultValue: 'Pricing campaign is force inactive',
				})}
			</Badge>
		)
	}
	if (isFinished) {
		return (
			<Badge size='sm' variant='warning' className='text-xs font-medium'>
				{t('pc.period.finished_at')} {end && intlFormat(end)}{' '}
				{end && `(${formatDistanceToNow(end, { addSuffix: true })})`}{' '}
			</Badge>
		)
	} else if (isNotStarted) {
		return (
			<Badge size='sm' variant='info' className='text-xs font-medium'>
				{t('pc.period.inactive_and_scheduled')}{' '}
				{start && intlFormat(start)}{' '}
			</Badge>
		)
	} else if (isActive) {
		return (
			<Badge size='sm' variant='success' className='text-xs font-medium'>
				{t('pc.period.is_active')}{' '}
				{end &&
					`(${differenceInDays(end, startOfDay(now))} ${t('days_left')})`}{' '}
			</Badge>
		)
	} else {
		return null
	}
}
const getPricingCampaignStatus = (now: Date, start?: Date, end?: Date) => {
	if (start && !end) {
		const isActive = isAfter(now, start)
		const isNotStarted = isBefore(now, start)

		return {
			isActive,
			isNotStarted,
			isFinished: false,
		}
	} else if (end && !start) {
		const isActive = isBefore(now, end)

		return {
			isActive,
			isNotStarted: false,
			isFinished: !isActive,
		}
	} else if (start && end) {
		const isActive = isBetween(now, start, end)
		const isNotStarted = isBefore(now, start)
		const isFinished = isAfter(now, end)

		return {
			isActive,
			isNotStarted,
			isFinished,
		}
	} else {
		return {
			isActive: false,
			isNotStarted: false,
			isFinished: false,
		}
	}
}
